import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";

import { useUserContext } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";
import { CustomVoice } from "../../context/types";
import { designVoice, getCustomVoicesByWorkspace, deleteCustomVoice } from "../../client/api";
import useStyles from "./styles";

const VoiceDesign: React.FC = () => {
  const classes = useStyles();
  const { user, currentWorkspace } = useUserContext();
  const { addNotification } = useNotification();

  const [voices, setVoices] = useState<CustomVoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Design dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [gender, setGender] = useState<"" | "male" | "female">("");
  const [locale, setLocale] = useState("");
  const [seed, setSeed] = useState(0);
  const [isDesigning, setIsDesigning] = useState(false);
  const [designedVoices, setDesignedVoices] = useState<CustomVoice[]>([]);

  // Audio preview state
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);

  const fetchVoices = async () => {
    if (!currentWorkspace?._id) return;
    try {
      setIsLoading(true);
      const response = await getCustomVoicesByWorkspace(currentWorkspace._id);
      setVoices(response.data);
    } catch (err: any) {
      console.error("Error fetching custom voices:", err);
      addNotification(err.response?.data?.message || "Failed to load voices", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVoices();
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?._id]);

  const filteredVoices = useMemo(() => {
    if (!searchQuery.trim()) return voices;
    const q = searchQuery.toLowerCase();
    return voices.filter(
      (v) =>
        v.name.toLowerCase().includes(q) ||
        v.language.toLowerCase().includes(q) ||
        v.gender.toLowerCase().includes(q) ||
        v.prompt.toLowerCase().includes(q)
    );
  }, [voices, searchQuery]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingVoiceId(null);
  };

  const playPreview = (voiceId: string, url: string | null) => {
    if (!url) {
      addNotification("No preview available for this voice", "warning");
      return;
    }
    if (playingVoiceId === voiceId) {
      stopAudio();
      return;
    }
    stopAudio();
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = () => setPlayingVoiceId(null);
    audio.onerror = () => {
      setPlayingVoiceId(null);
      addNotification("Failed to play preview", "error");
    };
    audio.play().catch(() => setPlayingVoiceId(null));
    setPlayingVoiceId(voiceId);
  };

  const handleOpenDialog = () => {
    setPrompt("");
    setGender("");
    setLocale("");
    setSeed(0);
    setDesignedVoices([]);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    stopAudio();
    setDialogOpen(false);
  };

  const handleDesign = async (nextSeed?: number) => {
    if (!currentWorkspace?._id || !user?._id) {
      addNotification("Missing workspace or user", "error");
      return;
    }
    if (!prompt.trim()) {
      addNotification("Please enter a voice description", "warning");
      return;
    }
    try {
      setIsDesigning(true);
      stopAudio();
      const effectiveSeed = nextSeed !== undefined ? nextSeed : seed;
      const response = await designVoice({
        prompt: prompt.trim(),
        gender: gender || undefined,
        locale: locale.trim() || undefined,
        seed: effectiveSeed,
        workspaceId: currentWorkspace._id,
        userId: user._id,
      });
      setDesignedVoices(response.data.voices);
      setSeed(response.data.seed);
      setVoices((prev) => [...response.data.voices, ...prev]);
    } catch (err: any) {
      console.error("Error designing voice:", err);
      addNotification(err.response?.data?.message || "Failed to design voice", "error");
    } finally {
      setIsDesigning(false);
    }
  };

  const handleTryAgain = () => {
    handleDesign(seed + 1);
  };

  const handleDelete = async (voice: CustomVoice) => {
    if (!window.confirm(`Delete "${voice.name}"?`)) return;
    try {
      stopAudio();
      await deleteCustomVoice(voice._id);
      setVoices((prev) => prev.filter((v) => v._id !== voice._id));
      addNotification("Voice deleted", "success");
    } catch (err: any) {
      console.error("Error deleting voice:", err);
      addNotification(err.response?.data?.message || "Failed to delete voice", "error");
    }
  };

  return (
    <Box className={classes.container}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.titleRow}>
          <GraphicEqIcon className={classes.titleIcon} />
          <Typography className={classes.title}>Voice Design</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          className={classes.createButton}
          disabled={!currentWorkspace?._id}
        >
          Create New Voice
        </Button>
      </Box>
      <Typography className={classes.subtitle}>
        Design custom voices from a natural language description. Describe the voice you want —
        tone, accent, energy — and we will suggest matches.
      </Typography>

      {/* Search */}
      {voices.length > 0 && (
        <Box className={classes.searchBar}>
          <SearchIcon sx={{ color: "#bbb", fontSize: 20 }} />
          <InputBase
            className={classes.searchInput}
            placeholder="Search by name, language, gender, or prompt..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
          />
        </Box>
      )}

      {/* Body */}
      {isLoading ? (
        <Box className={classes.loadingWrapper}>
          <CircularProgress sx={{ color: "#FFBF00" }} />
        </Box>
      ) : voices.length === 0 ? (
        <Box className={classes.emptyState}>
          <Box className={classes.emptyIconWrapper}>
            <GraphicEqIcon className={classes.emptyIcon} />
          </Box>
          <Typography className={classes.emptyTitle}>You haven't created any voices yet</Typography>
          <Typography className={classes.emptySubtitle}>
            Click "Create New Voice" to design a voice from a natural language description.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            className={classes.createButton}
          >
            Create New Voice
          </Button>
        </Box>
      ) : filteredVoices.length === 0 ? (
        <Box className={classes.emptyState}>
          <Typography className={classes.emptyTitle}>No matches for "{searchQuery}"</Typography>
        </Box>
      ) : (
        <Box className={classes.grid}>
          {filteredVoices.map((voice) => {
            const isPlaying = playingVoiceId === voice._id;
            return (
              <Box key={voice._id} className={classes.voiceCard}>
                <Box className={classes.voiceCardHeader}>
                  <Tooltip title={voice.name}>
                    <Typography className={classes.voiceName}>{voice.name}</Typography>
                  </Tooltip>
                  <Box className={classes.voiceActions}>
                    <IconButton
                      size="small"
                      className={classes.playButton}
                      onClick={() => playPreview(voice._id, voice.preview_audio_url)}
                      disabled={!voice.preview_audio_url}
                    >
                      {isPlaying ? (
                        <StopIcon fontSize="small" />
                      ) : (
                        <PlayArrowIcon fontSize="small" />
                      )}
                    </IconButton>
                    <IconButton
                      size="small"
                      className={classes.deleteButton}
                      onClick={() => handleDelete(voice)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Box className={classes.chipRow}>
                  <Chip label={voice.gender} size="small" className={classes.chip} />
                  <Chip label={voice.language} size="small" className={classes.chip} />
                  <Chip label={voice.type} size="small" className={classes.chip} />
                </Box>
                <Tooltip title={voice.prompt}>
                  <Typography className={classes.promptPreview}>"{voice.prompt}"</Typography>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Design Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle className={classes.dialogTitle}>
          <GraphicEqIcon sx={{ color: "#FFBF00" }} />
          Design a new voice
          <IconButton onClick={handleCloseDialog} sx={{ marginLeft: "auto" }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box>
            <Typography className={classes.fieldLabel}>Description</Typography>
            <TextField
              placeholder="e.g. warm, confident female narrator with a slight British accent"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              maxRows={5}
              inputProps={{ maxLength: 1000 }}
              className={classes.textArea}
            />
          </Box>
          <Box className={classes.inlineRow}>
            <Box className={classes.inlineField}>
              <Typography className={classes.fieldLabel}>Gender (optional)</Typography>
              <Select
                value={gender}
                onChange={(e) => setGender(e.target.value as "" | "male" | "female")}
                displayEmpty
                fullWidth
                size="small"
                sx={{
                  borderRadius: "12px",
                  fontFamily: "Lexend, sans-serif",
                  backgroundColor: "#fafafa",
                }}
              >
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
              </Select>
            </Box>
            <Box className={classes.inlineField}>
              <Typography className={classes.fieldLabel}>Locale (optional)</Typography>
              <TextField
                placeholder="e.g. en-US, pt-BR"
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                size="small"
                fullWidth
                className={classes.textArea}
              />
            </Box>
          </Box>

          {designedVoices.length > 0 && (
            <Box className={classes.resultsSection}>
              <Box className={classes.resultsHeader}>
                <Typography className={classes.resultsTitle}>
                  Matched voices (seed {seed})
                </Typography>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleTryAgain}
                  disabled={isDesigning}
                  sx={{
                    textTransform: "none",
                    fontFamily: "Lexend, sans-serif",
                    color: "#666",
                  }}
                >
                  Try again
                </Button>
              </Box>
              {designedVoices.map((voice) => {
                const isPlaying = playingVoiceId === voice._id;
                return (
                  <Box key={voice._id} className={classes.resultCard}>
                    <IconButton
                      size="small"
                      className={classes.playButton}
                      onClick={() => playPreview(voice._id, voice.preview_audio_url)}
                      disabled={!voice.preview_audio_url}
                    >
                      {isPlaying ? (
                        <StopIcon fontSize="small" />
                      ) : (
                        <PlayArrowIcon fontSize="small" />
                      )}
                    </IconButton>
                    <Box className={classes.resultInfo}>
                      <Typography className={classes.resultName}>{voice.name}</Typography>
                      <Typography className={classes.resultMeta}>
                        {voice.language} • {voice.gender}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "12px 24px 20px" }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              textTransform: "none",
              fontFamily: "Lexend, sans-serif",
              color: "#666",
            }}
          >
            {designedVoices.length > 0 ? "Done" : "Cancel"}
          </Button>
          <Button
            onClick={() => handleDesign()}
            variant="contained"
            disabled={isDesigning || !prompt.trim()}
            className={classes.createButton}
            startIcon={
              isDesigning ? <CircularProgress size={16} sx={{ color: "#000" }} /> : undefined
            }
          >
            {designedVoices.length > 0 ? "Design again" : "Design voice"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VoiceDesign;
